import { useId } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Lock } from 'lucide-react';
import { Button, type ButtonProps } from './Button';
import { cn } from '@/lib/cn';

/*
  A button for an action that is not built yet, or that this account cannot perform.

  The requirement is that it looks disabled, explains itself, and is still reachable. Those
  three pull against each other and the naive implementation fails the third:

    <Button disabled>Remote Control</Button>   // with a tooltip

  A disabled button is removed from the tab order and, in every browser, stops firing pointer
  and focus events. So a keyboard user cannot reach it, a screen reader user is never told it
  exists, and the tooltip that was supposed to explain the greying out can never open. The
  user is left with a control that has vanished and no reason given.

  So this uses `aria-disabled` instead of `disabled`. The button stays focusable and stays in
  the accessibility tree, activation is blocked in the handlers rather than by the browser,
  and the reason is bound with aria-describedby so it is announced on focus whether or not
  the tooltip is visible. The tooltip is then a sighted-user convenience rather than the only
  copy of the explanation.

  Blocking activation needs both handlers. onClick alone leaves Enter and Space working,
  because a focused button activates on keydown and the click that follows is synthetic; the
  keydown handler stops those two keys before they become an activation.
*/

export interface GatedButtonProps extends Omit<ButtonProps, 'disabled' | 'onClick'> {
  /**
   * Why the action is unavailable, in a sentence. Name the phase that delivers it, for
   * example "File transfer arrives in Phase 4." A bare "coming soon" is what this component
   * exists to prevent.
   */
  readonly reason: string;
  /** Short label for the badge inside the button, for example "Phase 2". */
  readonly badge?: string;
}

export function GatedButton({
  reason,
  badge,
  children,
  className,
  variant = 'secondary',
  ...rest
}: GatedButtonProps): React.JSX.Element {
  const reasonId = `gated-${useId()}`;

  return (
    <>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <Button
            variant={variant}
            aria-disabled="true"
            aria-describedby={reasonId}
            onClick={(event) => {
              event.preventDefault();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
              }
            }}
            className={cn(
              // Matches the disabled treatment in Button, but applied by class because the
              // element is not actually disabled. cursor-not-allowed rather than
              // pointer-events-none, because pointer-events-none would kill the tooltip too.
              'cursor-not-allowed opacity-55',
              className,
            )}
            {...rest}
          >
            <Lock className="size-3.5 shrink-0" aria-hidden="true" />
            {children}
            {badge === undefined ? null : (
              <span
                aria-hidden="true"
                className="ml-1 rounded border border-border bg-surface-sunken px-1 py-px text-[10px] font-semibold tracking-wide text-fg-subtle uppercase"
              >
                {badge}
              </span>
            )}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="bottom"
            sideOffset={6}
            className="animate-pop-in z-50 max-w-xs rounded-[var(--radius-control)] border border-border bg-surface-raised px-2.5 py-1.5 text-xs text-fg shadow-lg"
          >
            {reason}
            <Tooltip.Arrow className="fill-[var(--color-surface-raised)]" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>

      {/*
        The tooltip content lives in a portal and is only in the DOM while open, so it cannot
        be the aria-describedby target. This is, and it is always present.
      */}
      <span id={reasonId} className="sr-only">
        {reason}
      </span>
    </>
  );
}
